import React, { useState, useCallback, useMemo } from "react";
import TranslationService from "services/TranslationService";
import styles from "./TranslatableInput.module.css";

const TRANSLATABLE_FIELD_PATTERNS = [
  "diagnosis",
  "comments",
  "description",
  "notes",
  "medical.?history",
  "symptoms",
  "reason",
  "observation",
  "summary",
  "complaint",
  "condition",
  "treatment",
  "medication",
  "allerg",
  "instruction",
  "updates",
  "behavior",
  "sleep",
  "hemisphere",
];

const FIELD_PATTERN_REGEX = new RegExp(TRANSLATABLE_FIELD_PATTERNS.join("|"), "i");

export function isTranslatableField(name) {
  if (!name) return false;
  return FIELD_PATTERN_REGEX.test(name);
}

const SPANISH_PATTERN = /[áéíóúñü¿¡]|(\b(el|la|los|las|de|del|en|con|por|para|que|una?|es|su|se|al|lo|más|como|pero|esta|tiene|son|fue|está|hay|ser|han|muy|sin|sobre|entre|cuando|desde|donde|hacer|puede|todo|ya|nos|otro|fue|bien|así|después|antes|cada|parte|porque)\b)/gi;

function detectLang(text) {
  if (!text || !text.trim()) return "en";
  const matches = text.match(SPANISH_PATTERN);
  const score = matches ? matches.length : 0;
  const wordCount = text.split(/\s+/).length;
  return score / wordCount > 0.15 ? "es" : "en";
}

const GlobeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={styles.globeIcon}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export default function TranslatableInput({
  value = "",
  name,
  onTranslated,
  language,
  children,
}) {
  const [translating, setTranslating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [error, setError] = useState(null);

  const detected = useMemo(() => detectLang(value), [value]);
  const targetLang = detected === "es" ? "en" : "es";
  const btnLabel =
    value && value.trim()
      ? `${detected.toUpperCase()} → ${targetLang.toUpperCase()}`
      : "Translate";

  const handleTranslate = useCallback(async () => {
    if (!value || !value.trim() || translating) return;

    setTranslating(true);
    setError(null);

    try {
      const response = await TranslationService.translate({
        text: value,
        sourceLang: detected,
        targetLang,
      });

      const translated = response?.data?.translatedText;
      if (translated) {
        setPreview({ text: translated, from: detected, to: targetLang });
        setEditedText(translated);
      } else {
        setError("Translation returned empty result.");
      }
    } catch (err) {
      setError("Translation failed. Please try again.");
    } finally {
      setTranslating(false);
    }
  }, [value, detected, targetLang, translating]);

  const acceptTranslation = useCallback(() => {
    if (preview && onTranslated) {
      onTranslated(editedText, name);
    }
    setPreview(null);
    setEditedText("");
  }, [preview, editedText, onTranslated, name]);

  const cancelPreview = useCallback(() => {
    setPreview(null);
    setEditedText("");
  }, []);

  return (
    <div className={styles.translatableWrapper}>
      {children}

      {!preview && (
        <button
          type="button"
          className={`${styles.translateBtn} ${translating ? styles.translating : ""}`}
          onClick={handleTranslate}
          disabled={translating || !value || !value.trim()}
        >
          <GlobeIcon />
          <span>{translating ? "Translating..." : btnLabel}</span>
        </button>
      )}

      {preview && (
        <div className={styles.previewOverlay}>
          <div className={styles.previewLabel}>
            {preview.from.toUpperCase()} → {preview.to.toUpperCase()} Translation
          </div>
          <div className={styles.previewText}>
            <textarea
              className={styles.previewTextarea}
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
            />
          </div>
          <div className={styles.previewActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={cancelPreview}
            >
              ✕ Cancel
            </button>
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={acceptTranslation}
            >
              ✓ Accept
            </button>
          </div>
        </div>
      )}

      {error && <div className={styles.errorMsg}>{error}</div>}
    </div>
  );
}
